import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { toast } from 'sonner';
import { 
  Upload, FileImage, FileText, Loader2, Check, Sparkles, X, FolderOpen, 
  UtensilsCrossed, AlertCircle, Pencil, Trash2, ChevronDown, ChevronUp, Euro
} from 'lucide-react';

// Steps: upload -> review -> done
const STEP_UPLOAD = 'upload';
const STEP_REVIEW = 'review';
const STEP_DONE = 'done';

const MenuImport = ({ open, onClose, menus = [], onImportComplete }) => {
  const [step, setStep] = useState(STEP_UPLOAD);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [extraction, setExtraction] = useState(null); // editable extraction data
  const [result, setResult] = useState(null);
  const [collapsedCats, setCollapsedCats] = useState({});
  const fileRef = useRef(null);

  const reset = () => {
    setStep(STEP_UPLOAD);
    setFile(null);
    setPreview(null);
    setSelectedMenu('');
    setExtracting(false);
    setConfirming(false);
    setExtraction(null);
    setResult(null);
    setCollapsedCats({});
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(f.type)) { toast.error('Format non supporté. Utilisez JPG, PNG, WebP ou PDF.'); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error('Fichier trop volumineux. Maximum 10MB.'); return; }
    setFile(f);
    setResult(null);
    setExtraction(null);
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(f);
    } else { setPreview(null); }
  };

  // Step 1: Extract with AI
  const handleExtract = async () => {
    if (!file) return;
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/ai/extract-menu', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      setExtraction(res.data.extraction);
      setStep(STEP_REVIEW);
      toast.success('Menu extrait ! Vérifiez et corrigez si nécessaire.');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'extraction");
    } finally { setExtracting(false); }
  };

  // Step 2: Confirm import
  const handleConfirm = async () => {
    if (!extraction) return;
    setConfirming(true);
    try {
      const payload = {
        categories: extraction.categories,
        menu_id: selectedMenu && selectedMenu !== 'all' ? selectedMenu : null
      };
      const res = await api.post('/ai/confirm-import', payload);
      setResult(res.data);
      setStep(STEP_DONE);
      toast.success(res.data.message);
      if (onImportComplete) onImportComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'import");
    } finally { setConfirming(false); }
  };

  // Edit helpers
  const updateDish = (catIdx, dishIdx, field, value) => {
    setExtraction(prev => {
      const cats = [...prev.categories];
      const dishes = [...cats[catIdx].dishes];
      dishes[dishIdx] = { ...dishes[dishIdx], [field]: value };
      cats[catIdx] = { ...cats[catIdx], dishes };
      return { ...prev, categories: cats };
    });
  };

  const removeDish = (catIdx, dishIdx) => {
    setExtraction(prev => {
      const cats = [...prev.categories];
      const dishes = cats[catIdx].dishes.filter((_, i) => i !== dishIdx);
      if (dishes.length === 0) {
        return { ...prev, categories: cats.filter((_, i) => i !== catIdx) };
      }
      cats[catIdx] = { ...cats[catIdx], dishes };
      return { ...prev, categories: cats };
    });
  };

  const removeCategory = (catIdx) => {
    setExtraction(prev => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== catIdx)
    }));
  };

  const updateCategoryName = (catIdx, name) => {
    setExtraction(prev => {
      const cats = [...prev.categories];
      cats[catIdx] = { ...cats[catIdx], name };
      return { ...prev, categories: cats };
    });
  };

  const toggleCollapse = (catIdx) => {
    setCollapsedCats(prev => ({ ...prev, [catIdx]: !prev[catIdx] }));
  };

  const totalDishes = extraction?.categories?.reduce((sum, c) => sum + c.dishes.length, 0) || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="menu-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-orange-500" />
            {step === STEP_UPLOAD && "Importer un menu avec l'IA"}
            {step === STEP_REVIEW && "Vérifiez votre menu"}
            {step === STEP_DONE && "Import terminé"}
          </DialogTitle>
          <DialogDescription>
            {step === STEP_UPLOAD && "Uploadez une photo ou un PDF de votre menu papier."}
            {step === STEP_REVIEW && "Corrigez les noms, prix ou supprimez les éléments incorrects avant de valider."}
            {step === STEP_DONE && "Vos plats ont été ajoutés à votre menu."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">

          {/* ===== STEP 1: UPLOAD ===== */}
          {step === STEP_UPLOAD && (
            <>
              <div
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all hover:border-orange-400 hover:bg-orange-50/50 ${
                  file ? 'border-orange-300 bg-orange-50/30' : 'border-gray-200'
                }`}
                data-testid="menu-import-dropzone"
              >
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="hidden" onChange={handleFile} data-testid="menu-import-file-input" />
                {file ? (
                  <div className="space-y-3">
                    {preview ? <img src={preview} alt="Aperçu" className="max-h-48 mx-auto rounded-lg shadow" /> : <FileText className="h-12 w-12 text-orange-500 mx-auto" />}
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(0)} Ko</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); }}>
                      <X className="h-4 w-4 mr-1" /> Changer
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex justify-center gap-3">
                      <FileImage className="h-10 w-10 text-gray-300" />
                      <FileText className="h-10 w-10 text-gray-300" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">Cliquez pour choisir un fichier</p>
                    <p className="text-xs text-gray-400">JPG, PNG, WebP ou PDF (max 10MB)</p>
                  </div>
                )}
              </div>

              {menus.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">Associer à un menu (optionnel)</Label>
                  <Select value={selectedMenu} onValueChange={setSelectedMenu}>
                    <SelectTrigger data-testid="menu-import-select"><SelectValue placeholder="Tous les menus" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les menus</SelectItem>
                      {menus.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button onClick={handleExtract} disabled={!file || extracting}
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold" data-testid="menu-import-submit">
                {extracting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Analyse en cours... (30-60s)</> : <><Sparkles className="mr-2 h-5 w-5" />Analyser le menu</>}
              </Button>
              {extracting && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                  <p className="text-xs text-blue-700 text-center">L'IA analyse votre menu... Cela peut prendre jusqu'à 1 minute.</p>
                </div>
              )}
            </>
          )}

          {/* ===== STEP 2: REVIEW ===== */}
          {step === STEP_REVIEW && extraction && (
            <>
              {/* Summary bar */}
              <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-xl px-4 py-2.5">
                <span className="text-sm font-medium text-orange-800">
                  {extraction.categories.length} catégorie{extraction.categories.length > 1 ? 's' : ''} · {totalDishes} plat{totalDishes > 1 ? 's' : ''}
                </span>
                <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-xs">Modifiable</Badge>
              </div>

              {/* Editable categories & dishes */}
              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1" data-testid="menu-import-review">
                {extraction.categories.map((cat, catIdx) => (
                  <div key={catIdx} className="border border-gray-200 rounded-xl overflow-hidden">
                    {/* Category header */}
                    <div className="flex items-center gap-2 bg-gray-50 px-3 py-2">
                      <button onClick={() => toggleCollapse(catIdx)} className="text-gray-400 hover:text-gray-600">
                        {collapsedCats[catIdx] ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                      </button>
                      <FolderOpen className="h-4 w-4 text-orange-500 flex-shrink-0" />
                      <Input value={cat.name} onChange={(e) => updateCategoryName(catIdx, e.target.value)}
                        className="h-8 text-sm font-semibold border-0 bg-transparent p-0 focus-visible:ring-0 flex-1"
                        data-testid={`review-cat-name-${catIdx}`} />
                      <span className="text-xs text-gray-400 flex-shrink-0">{cat.dishes.length}</span>
                      <button onClick={() => removeCategory(catIdx)} className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0" data-testid={`review-cat-delete-${catIdx}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {/* Dishes */}
                    {!collapsedCats[catIdx] && (
                      <div className="divide-y divide-gray-100">
                        {cat.dishes.map((dish, dishIdx) => (
                          <div key={dishIdx} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50/50 group">
                            <div className="flex-1 min-w-0">
                              <Input value={dish.name} onChange={(e) => updateDish(catIdx, dishIdx, 'name', e.target.value)}
                                className="h-7 text-sm border-0 bg-transparent p-0 focus-visible:ring-0 font-medium"
                                data-testid={`review-dish-name-${catIdx}-${dishIdx}`} />
                              {dish.description && (
                                <Input value={dish.description} onChange={(e) => updateDish(catIdx, dishIdx, 'description', e.target.value)}
                                  className="h-6 text-xs text-gray-400 border-0 bg-transparent p-0 focus-visible:ring-0 mt-0.5" />
                              )}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Input type="number" step="0.01" min="0" value={dish.price || ''}
                                onChange={(e) => updateDish(catIdx, dishIdx, 'price', parseFloat(e.target.value) || 0)}
                                className="h-7 w-20 text-sm text-right font-semibold text-orange-600 border-gray-200 rounded-lg px-2"
                                data-testid={`review-dish-price-${catIdx}-${dishIdx}`} />
                              <Euro className="h-3.5 w-3.5 text-gray-400" />
                            </div>
                            <button onClick={() => removeDish(catIdx, dishIdx)}
                              className="text-gray-200 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                              data-testid={`review-dish-delete-${catIdx}-${dishIdx}`}>
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {extraction.categories.length === 0 && (
                <div className="text-center py-6 text-gray-400">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                  <p className="text-sm">Tous les éléments ont été supprimés</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => { setStep(STEP_UPLOAD); setExtraction(null); }}
                  className="flex-1 rounded-xl" data-testid="review-back-btn">
                  Retour
                </Button>
                <Button onClick={handleConfirm} disabled={confirming || totalDishes === 0}
                  className="flex-1 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="review-confirm-btn">
                  {confirming ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Création...</> : <><Check className="mr-2 h-4 w-4" />Valider ({totalDishes} plats)</>}
                </Button>
              </div>
            </>
          )}

          {/* ===== STEP 3: DONE ===== */}
          {step === STEP_DONE && result && (
            <div className="space-y-4" data-testid="menu-import-result">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                <Check className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                <p className="font-semibold text-emerald-800">{result.message}</p>
              </div>

              {result.categories?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <FolderOpen className="h-4 w-4 text-orange-500" /> Nouvelles catégories ({result.categories_created})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {result.categories.map((c, i) => <Badge key={i} variant="secondary" className="text-xs">{c.name}</Badge>)}
                  </div>
                </div>
              )}

              {result.dishes?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <UtensilsCrossed className="h-4 w-4 text-orange-500" /> Plats créés ({result.dishes_created})
                  </p>
                  <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
                    {result.dishes.map((d, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-1.5 text-sm">
                        <div className="min-w-0">
                          <span className="font-medium text-gray-900 truncate block">{d.name}</span>
                          <span className="text-xs text-gray-400">{d.category}</span>
                        </div>
                        {d.price > 0 && <span className="text-sm font-semibold text-orange-600 flex-shrink-0 ml-2">{d.price.toFixed(2)} €</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={reset} className="flex-1 rounded-xl" data-testid="menu-import-new">
                  <Upload className="mr-2 h-4 w-4" /> Importer un autre
                </Button>
                <Button onClick={handleClose} className="flex-1 rounded-xl bg-orange-500 hover:bg-orange-600 text-white" data-testid="menu-import-done">
                  Terminé
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MenuImport;
